// ---------------------------------------------------------------------------
// Navigation intent — a leaf module that imports NOTHING.
// ---------------------------------------------------------------------------
// The stores need to say "this mutation is a real navigation, record it in
// history" without importing the router (router → stores → navIntent, no cycle).
//
// Two rules that make history corruption structurally impossible:
//  1. `push` only ever comes from a DECLARED user intent (mark/tx). The
//     store→URL reconciler is replace-only, so a correction after a refused or
//     altered apply costs one silent rewrite — never a stack corruption.
//  2. The default is `replace`. A write the code forgot to classify costs a
//     missing Back step; it can never trap the user or spam the stack.
// ---------------------------------------------------------------------------

type Mode = 'push' | 'replace';

let mode: Mode = 'replace';
let depth = 0;
let flush: (() => void) | null = null;

export const nav = {
  /** How the NEXT history write is recorded. Ignored inside a tx (the tx wins). */
  mark(m: Mode): void {
    if (depth === 0) mode = m;
  },
  /** Collapse several store mutations into ONE history write.
   *  e.g. Inbox → click a task = selectArea + open + close-overlay = one entry. */
  tx<T>(m: Mode, fn: () => T): T {
    depth++;
    mode = m;
    try {
      return fn();
    } finally {
      if (--depth === 0) flush?.();
    }
  },
  get mode(): Mode {
    return mode;
  },
  /** True while a tx is open — the reconciler must not write mid-transaction. */
  suspended(): boolean {
    return depth > 0;
  },
  reset(): void {
    mode = 'replace';
  },
  /** The router registers its reconciler here. */
  _bind(f: () => void): void {
    flush = f;
  },
};
